import type { NavigateFunction } from "react-router-dom";
import type { ButtonClickInfo } from "./cellRenderer.tsx";
import type { TransactionOpsArgs } from "../transactionOps/transactionOpsModal.tsx";
import type { CurrentRentalsArgs } from "../transactionOps/currentRentalsModal.tsx";
import { ParamType, type InputParam } from "../../types/grid.ts";
import { getPagePath } from "../../pages/pageRegistry.ts";

/** Append query params to a path, using ? or & as appropriate. */
function appendQuery(basePath: string, params: Record<string, string>): string {
  const sep = basePath.includes("?") ? "&" : "?";
  const qs = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return `${basePath}${sep}${qs}`;
}

/** Look up a cell value by trying multiple column names (case-insensitive fallback). */
function cell(row: unknown[], colIndex: Record<string, number>, ...names: string[]): string {
  for (const n of names) {
    const idx = colIndex[n] ?? colIndex[n.toLowerCase()];
    if (idx !== undefined && row[idx] != null) return String(row[idx]);
  }
  return "";
}

/** Payload for {@link ButtonDispatchCallbacks.onReserveCancel} — case 121.
 *  The host shows the confirm dialog, then commits a cell update of
 *  `setColumn = setValue` keyed by `keyParams`, and refreshes the grid. */
export interface ReserveCancelArgs {
  setColumn: string;
  setValue: string;
  keyParams: InputParam[];
  reservationCode: string;
  username: string;
}

export interface ButtonDispatchCallbacks {
  onTransactionOps?: (args: TransactionOpsArgs) => void;
  onPopup?: (path: string) => void;
  /** Opens the Product Lock Times modal — case 37. Mirrors WebAdmin2's
   *  `ProductLockPopup`. KioskID is forwarded for callers that need it; the
   *  current modal pins it to 1, matching legacy. */
  onProductLock?: (productId: number, kioskId: number) => void;
  /** Opens the rental "Operations" modal — case 40. Mirrors WebAdmin2's
   *  `currentRentalsPopup` (Return + Open Refund Menu). */
  onCurrentRentals?: (args: CurrentRentalsArgs) => void;
  /** Cancels a reservation — case 121. Mirrors WebAdmin2's reserveStatus
   *  cell-update; the host confirms then commits + refreshes. */
  onReserveCancel?: (args: ReserveCancelArgs) => void;
}

/**
 * Dispatches a grid button action based on the button's page name and type.
 * Returns true if the action was handled, false if unsupported.
 */
export function dispatchButtonAction(
  info: ButtonClickInfo,
  row: unknown[],
  colIndex: Record<string, number>,
  navigate: NavigateFunction,
  callbacks?: ButtonDispatchCallbacks,
  keyColumns: string[] = [],
): boolean {
  const { pageName, type, label, rowData } = info;
  // NOTE: info.rowData is the BUTTON's own encoded param array (server-built into the button
  // cell value, split on '¤'), NOT the grid row. These positional reads index that param
  // list, so they are independent of grid column reordering/exclusion — do not "fix" them to
  // grid-column-name lookups. (Grid-row reads in this dispatcher go through cell(row, …).)
  const primaryId = rowData[0] ?? "";

  switch (type) {
    case 0:
      return handleType0(pageName, primaryId, navigate, callbacks);

    case 12: {
      if (!rowData[1]) return false;
      const args: TransactionOpsArgs = {
        transactionID: parseInt(rowData[1] ?? "0", 10),
        customerID: parseInt(rowData[0] ?? "0", 10),
        discID: parseInt(rowData[2] ?? "0", 10),
        rfid: rowData[6] ?? "",
        instantID: cell(row, colIndex, "sInstantTransactionID", "instantId", "sInstantID", "InstantID"),
        productID: parseInt(cell(row, colIndex, "iProductID", "productId") || "0", 10),
        productName: cell(row, colIndex, "sProduct"),
        kioskID: parseInt(cell(row, colIndex, "iKioskID", "kioskId") || "0", 10),
        rowID: parseInt(cell(row, colIndex, "iTransactionRowID") || "0", 10),
        isLocked: cell(row, colIndex, "bLocked").toLowerCase() === "true",
        hasRental: false,
      };
      callbacks?.onTransactionOps?.(args);
      return true;
    }

    case 30: {
      const sid = cell(row, colIndex, "InstantID", "sInstantTransactionID", "sInstantID");
      const tid = cell(row, colIndex, "TransactionID", "iTransactionID");
      const evPath = appendQuery(getPagePath("editcustomerevents"), { sid, tid });
      if (callbacks?.onPopup) {
        callbacks.onPopup(evPath);
      } else {
        navigate(evPath);
      }
      return true;
    }

    case 37: {
      const productId = parseInt(cell(row, colIndex, "ProductID", "iProductID") || "0", 10);
      const kioskId = parseInt(cell(row, colIndex, "KioskID", "iKioskID") || "0", 10);
      if (callbacks?.onProductLock) {
        callbacks.onProductLock(productId, kioskId);
        return true;
      }
      return false;
    }

    case 38: {
      const id = cell(row, colIndex, "ID");
      const path = appendQuery(getPagePath("editreplenish"), { id });
      if (callbacks?.onPopup) {
        callbacks.onPopup(path);
      } else {
        navigate(path);
      }
      return true;
    }

    case 31: {
      // Edit InvApp order. Full-page nav (form page, not popup-hosted).
      navigate(appendQuery(getPagePath("invappeditorder"), { id: cell(row, colIndex, "ID") }));
      return true;
    }

    case 32: {
      // New InvApp warehouse.
      navigate(appendQuery(getPagePath("newwarehouse"), { id: cell(row, colIndex, "ID") }));
      return true;
    }

    case 33: {
      // New InvApp pickup spot.
      navigate(appendQuery(getPagePath("newpickup"), { id: cell(row, colIndex, "ID") }));
      return true;
    }

    case 40: {
      // "Operations" — opens the rental operations modal (Return / Open Refund
      // Menu). Mirrors WebAdmin2 currentRentalsPopup.
      if (!callbacks?.onCurrentRentals) return false;
      callbacks.onCurrentRentals({
        transactionRowID: parseInt(cell(row, colIndex, "iTransactionRowID") || "0", 10),
        instantID: cell(row, colIndex, "sInstantTransactionID"),
        kioskID: parseInt(cell(row, colIndex, "iKioskId", "iKioskID") || "0", 10),
        productID: parseInt(cell(row, colIndex, "iProductID") || "0", 10),
        transactionID: parseInt(cell(row, colIndex, "iTransactionID") || "0", 10),
      });
      return true;
    }

    case 121: {
      // Reservation cancel — sets reserveStatus to the button's page name
      // ("cancel"). Mirrors WebAdmin2 ButtonHandler case 121: confirm, then a
      // cell update keyed by the grid's KeyColumns + a Set on reserveStatus.
      if (pageName.toLowerCase() !== "cancel" || !callbacks?.onReserveCancel) return false;
      const keyParams: InputParam[] = keyColumns.map((kc) => ({
        sColumn: kc,
        oValue: cell(row, colIndex, kc),
        Type: ParamType.Equal,
      }));
      callbacks.onReserveCancel({
        setColumn: "reserveStatus",
        setValue: pageName,
        keyParams,
        reservationCode: cell(row, colIndex, "reservationCode"),
        username: cell(row, colIndex, "username"),
      });
      return true;
    }

    default:
      console.warn(
        "[buttonDispatcher] Unsupported button click — no handler for this (type, pageName) pair.",
        {
          pageName,
          type,
          label,
          rowId: info.rowData[0],
          rowData: info.rowData,
          columns: Object.keys(colIndex).slice(0, 12),
        },
      );
      return false;
  }
}

/** Type 0: Navigation / popup actions based on page name */
function handleType0(
  pageName: string,
  primaryId: string,
  navigate: NavigateFunction,
  callbacks?: ButtonDispatchCallbacks,
): boolean {
  switch (pageName.toLowerCase()) {
    case "edit_product":
      navigate(appendQuery(getPagePath("searchEditProduct"), { edit: primaryId }));
      return true;

    case "editemailtemplate": {
      // Open the email-template editor as a dialog-over-grid when the host grid
      // supports popups; otherwise fall back to full-page navigation.
      const path = appendQuery(getPagePath("editemailtemplate"), { id: primaryId });
      if (callbacks?.onPopup) callbacks.onPopup(path);
      else navigate(path);
      return true;
    }

    case "ticket_history":
      navigate(appendQuery(getPagePath("tickethistory"), { id: primaryId }));
      return true;

    case "flatrate_subscriber_history":
      navigate(appendQuery(getPagePath("flatrateSubscribersDetail"), { iFlatRateSubscriptionId: primaryId }));
      return true;

    case "golf_game_info":
      navigate(appendQuery(getPagePath("golfgameinfo"), { id: primaryId }));
      return true;

    default:
      navigate(appendQuery(getPagePath(pageName), { id: primaryId }));
      return true;
  }
}
